import React, {useState, useCallback, useEffect, useMemo} from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  TextInput,
  Alert,
  KeyboardAvoidingView,
  Platform,
  TouchableWithoutFeedback,
  Keyboard,
  ScrollView,
} from 'react-native';
import {styles} from './styles';

import {useNavigation} from '@react-navigation/native';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import {Images} from '../../constants/Images';
import {calculatePriceBasedOnQuantity} from '../../utils/price';
import {useBagStore} from '../../store/BagStore';
import Toast from '../../components/Toast/Toast';
import {Colors} from '../../constants/Colors';

import SearchHeader from '../../components/SearchHeader/SearchHeader';
import PressCloseKeyboard from '../../components/PressCloseKeyboard/PressCloseKeyboard';
import ProductsService from '../../services/ProductsService/ProductsService';
import {useUserStore} from '../../store/UserStore';
import ComboProductsList from './ComboProductsList/ComboProductsList';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import AviseMeModal from '../../components/AviseMeModal/AviseMeModal';
import StockNotificationService from '../../services/StockNotificationService/StockNotificationService';
import {useIsDesktop} from '../../hooks/useIsDesktop';

const ProductSkeleton = () => (
  <View style={{padding: 20}}>
    <View
      style={{
        height: 200,
        backgroundColor: Colors.GrayPallete.lightgrayTwo,
        borderRadius: 8,
        marginBottom: 20,
      }}
    />
    <View
      style={{
        height: 20,
        backgroundColor: Colors.GrayPallete.lightgrayTwo,
        marginBottom: 10,
        borderRadius: 4,
      }}
    />
    <View
      style={{
        height: 20,
        backgroundColor: Colors.GrayPallete.lightgrayTwo,
        marginBottom: 10,
        width: '80%',
        borderRadius: 4,
      }}
    />
    <View
      style={{
        height: 20,
        backgroundColor: Colors.GrayPallete.lightgrayTwo,
        marginBottom: 10,
        width: '60%',
        borderRadius: 4,
      }}
    />
    <View
      style={{
        height: 40,
        backgroundColor: Colors.GrayPallete.lightgrayTwo,
        marginVertical: 20,
        borderRadius: 8,
      }}
    />
  </View>
);

const ProductScreen = ({route}) => {
  const isDesktop = useIsDesktop();
  const {item: initialItem, sku, fromBanner, fromBag} = route.params;
  const [item, setItem] = useState<IProduct | null>(initialItem || null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const {user} = useUserStore();

  const [toast, setToast] = useState<boolean>(false);
  const [price, setPrice] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [discountedPrice, setDiscountedPrice] = useState<number | null>(null);

  const [isCombo, setIsCombo] = useState<boolean>(false);
  const max = item?.maximum ?? item?.estoque ?? 0;

  const handleAdd = (value: number) => {
    setCount(prev => Math.max(0, prev + value));
  };

  const [aviseMeModal, setAviseMeModal] = useState<boolean>(false);
  const handleCloseModal = () => {
    setAviseMeModal(false);
  };
  const openModal = () => {
    setAviseMeModal(true);
  };

  const products = useBagStore(state => state.products);
  const {addProduct, updateProductQuantity} = useBagStore();

  useEffect(() => {
    if (item) {
      const {price: nPrice, discountedPrice: ndiscountedPrice} =
        calculatePriceBasedOnQuantity(item, count);
      setPrice(nPrice);
      setDiscountedPrice(ndiscountedPrice);
      let combo = item?.category || item?.categoria;
      setIsCombo(combo === 'COMBO' && (item?.products || item?.comboProducts));

      if (item.maximum != null) {
        item.estoque = item.maximum;
      }
    }
  }, [count, item]);

  const originalComboPrice = useMemo(() => {
    if (!isCombo) return null;

    const comboItems =
      Array.isArray(item?.products) && item.products.length > 0
        ? item.products
        : Array.isArray(item?.comboProducts)
        ? item.comboProducts
        : null;

    if (!comboItems) return null;

    return comboItems.reduce(
      (acc, prod) =>
        prod.operation === 'ZS03' ? acc : acc + (prod.price ?? 0) * prod.quantity,
      0,
    );
  }, [isCombo, item?.products, item?.comboProducts]);
  
  
const discountPercentage = useMemo(() => {
  if (!isCombo || !originalComboPrice) {
    return null;
  }

    const discount = originalComboPrice - (discountedPrice || price);

  const percentage = (discount / originalComboPrice) * 100;

    // Sem desconto real (ou combo mais caro) nao ha indicativo a exibir.
    return percentage > 0 ? percentage : null;
  }, [isCombo, originalComboPrice, discountedPrice, price]);

  useEffect(() => {
    if (!item || !item.sku) return;
    setCount(products.find(prod => prod.sku === item.sku)?.quantity || 0);
  }, [products, item?.sku]);

  const loadProduct = async () => {
    try {
      if (!sku) {
        Alert.alert('Erro', 'SKU do produto não fornecido');
        return null;
      }

      // Filtro exato por SKU. Pelo `search` (busca textual em descrição,
      // marca, submenu...) com per-page 1, qualquer produto que contivesse o
      // termo podia vir no lugar do SKU pedido.
      const response = await ProductsService.catalogProductsGet(
        user?.clientCode,
        1,
        1,
        undefined,
        undefined,
        undefined,
        undefined,
        sku,
      );

      return response.data?.products?.[0] || null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      setLoadingProduct(true);
      const product = await loadProduct();

      if (product) {
        setItem(product);
        setPrice(product.preco);
        setDiscountedPrice(product.desconto);

        const bagProduct = products.find(prod => prod?.sku === product?.sku);
        setCount(bagProduct?.quantity || 0);
      } else {
        Alert.alert('Produto não encontrado');
      }

      setLoadingProduct(false);
    };
    if (!initialItem) {
      fetchProduct();
    }
  }, [sku, initialItem]);

  const {goBack, navigate} = useNavigation<NavigationProps>();

  const formatPrice = useCallback((price: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(parseFloat(price));
  }, []);

  const getStockStatusColor = useCallback((stock: number) => {
    if (!item) return 'gray';
    if (stock > 200) {
      return '#21ce2f';
    } else if (stock > 0 && stock <= 200) {
      return '#fb6340';
    } else {
      return '#ec1f24';
    }
  }, []);

    const formatPriceRanges = useCallback((item: IProduct, count: number) => {
      if (!item || !item.faixaVolume2) return [];
      const ranges = [];
    
    for (let i = 2; i <= 5; i++) {
      const volume = item[`faixaVolume${i}`];
      const rangePrice = item[`kdkg${i}`];
      if (volume && rangePrice) {
        const quantityNeeded = volume - count;
  
        ranges.push({
          quantityNeeded: Math.max(quantityNeeded, 0),
          rangePrice: rangePrice.toFixed(2).replace('.', ','),
          isActive: quantityNeeded > 0,
          volume,
        });
      }
    }
    return ranges;
  }, []);

  const createNotification =  useCallback((item: IProduct)  => {
    StockNotificationService.createStockNotification(item.sku, item.descricao);
  }, []);

  const priceRanges = useMemo(() => formatPriceRanges(item, count), [item, count, formatPriceRanges]);

  const handleAddBag = (product: IProduct, quantity: number) => {
    if (quantity > max) {
      quantity = max;
    }

    const key = (product as any)?.cartKey ?? product.sku;

    if (products.find(prod => ((prod as any).cartKey ?? prod.sku) === key)) {
      updateProductQuantity(key, quantity);
    } else {
      addProduct({
        ...product,
        fromBanner: fromBanner || false,
        quantity: quantity,
      });
    }
    setToast(true);

    setTimeout(() => {
      if (fromBag) {
        goBack();
      } else {
        // Navigate to main tab and open Home tab instead of direct Home route
        (navigate as any)(AppRoutes.MAIN_TAB_SCREEN, { screen: 'Home' });
      }
    }, 1500);
  };

  if (loadingProduct) {
    return (
      <PressCloseKeyboard>
        <ProductSkeleton />
      </PressCloseKeyboard>
    );
  }

  if (!item) {
    return (
      <PressCloseKeyboard>
        <View style={styles.container}>
          <SearchHeader onPress={goBack} title="Detalhes do Produto" />
          <Text style={styles.errorText}>Produto não encontrado</Text>
        </View>
      </PressCloseKeyboard>
    );
  }

  return (
    <PressCloseKeyboard>
      <KeyboardAvoidingView
        keyboardVerticalOffset={Platform.OS === 'ios' ? 40 : 0}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{flex: 1}}>
        <ScrollView style={[styles.container, isDesktop && { maxWidth: 700, alignSelf: 'center', width: '100%' }]}>
          {toast && <Toast title="Carrinho atualizado!" />}

          <SearchHeader
            onPress={goBack}
            title={`Detalhes do ${isCombo ? 'Combo' : 'Produto'}`}
          />

          <View>
            <Image
              source={item.image ? {uri: item.image} : Images.emptyImgIcon}
              style={[styles.imageStyle, isDesktop && { width: 200, height: 200 }]}
            />
            {isCombo && (<Image source={Images.saleIcon} style={styles.overlayIcon} />)}
          </View>
          <Text numberOfLines={4} style={styles.description}>
            {item?.descricao + ' ' + item?.complemento.replace('- R$', '')}
          </Text>

                        {item?.estoque > 0 && priceRanges.length > 0 && (
                <View style={styles.priceRangesGrid}>
                  {priceRanges.map((range, index) => {
                    const isSelected = count >= range.volume && (index === priceRanges.length - 1 || count < priceRanges[index + 1]?.volume);
              
                    return (
                      <TouchableOpacity
                        key={index}
                        style={[
                          styles.priceRangeCard,
                          !range.isActive && styles.disabledCard,
                          isSelected && styles.selectedCard,
                        ]}
                        onPress={() => {
                          if (range.isActive) {
                            setCount(Math.min(range.volume, item?.maximum || item?.estoque));
                          }
                        }}
                        disabled={!range.isActive}
                      >
                        <Text style={[styles.priceRangeText, !range.isActive && styles.disabledText]}>
                          {range.isActive
                            ? `Adicione ${range.quantityNeeded} para pagar`
                            : `Faixa atingida`}
                        </Text>
                        <Text style={[styles.priceRangeValue, !range.isActive && styles.disabledText]}>
                          R$ {range.rangePrice}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}

          <View style={styles.infoContainer}>
            {isCombo && originalComboPrice && discountPercentage !== null ? (
              <View style={{flexDirection: 'column', marginBottom: 8}}>
                <Text
                  style={[
                    styles.price,
                    {
                      textDecorationLine: 'line-through',
                    },
                  ]}>
                  De: {formatPrice(originalComboPrice?.toFixed(2)?.toString())}
                </Text>

                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  <Text
                    numberOfLines={1}
                    style={[styles.price, {color: Colors.RedPallete.red}]}>
                    Por:{' '}
                    {formatPrice(
                      discountedPrice
                        ? discountedPrice?.toFixed(2)?.toString()
                        : price?.toFixed(2)?.toString(),
                    )}
                  </Text>
                  {discountPercentage !== null && (
                    <Text
                      style={{
                        marginLeft: 8,
                        color: Colors.RedPallete.red,
                        fontWeight: 'bold',
                        fontSize: 16,
                      }}>
                      -{Math.round(discountPercentage)}%
                    </Text>
                  )}
                </View>
              </View>
            ) : (
              <Text numberOfLines={1} style={styles.price}>
                {formatPrice(
                  discountedPrice
                    ? discountedPrice?.toFixed(2)?.toString()
                    : price?.toFixed(2)?.toString(),
                )}
              </Text>
            )}

            <Text
              style={[
                styles.hasItem,
                {color: getStockStatusColor(item?.estoque)},
              ]}>
              {item?.estoque > 200
                ? 'Disponível'
                : item?.estoque > 0 && item?.estoque <= 200
                ? 'Estoque baixo'
                : 'Indisponível'}
            </Text>
          </View>

          {item?.estoque > 0 && (
            <>
              <Text style={styles.label}>
                Quantos {`${isCombo ? 'combos' : 'produtos'}`} você quer
                comprar?
              </Text>

              <View style={styles.quantityContainer}>
                <TouchableOpacity
                  onPress={() => handleAdd(-1)}
                  disabled={toast}>
                  <Text style={styles.button}>-</Text>
                </TouchableOpacity>

                <TextInput
                  style={styles.input}
                  value={count.toString()}
                  keyboardType="numeric"
                  returnKeyType="done"
                  onChangeText={text => {
                    const newValue = Number(text.replace(/[^0-9]/g, '')) || 0;
                    setCount(
                      Math.min(newValue, item?.maximum || item?.estoque),
                    );
                  }}
                />

                {count < max ? (
                  <TouchableOpacity
                    onPress={() => handleAdd(1)}
                    disabled={toast}>
                    <Text style={styles.button}>+</Text>
                  </TouchableOpacity>
                ) : (
                  <Text style={styles.button}> </Text>
                )}
              </View>

              <Text style={styles.helperText}>
                Digite quantos {`${isCombo ? 'combos' : 'produtos'}`} você quer
                comprar
              </Text>

              <View style={styles.quickAddContainer}>
                {[5, 10, 20].map(item => (
                  <TouchableOpacity
                    disabled={toast || max ? count + item > max : false}
                    key={item}
                    onPress={() => handleAdd(item)}>
                    <Text
                      style={[
                        styles.quickAddButton,
                        {
                          backgroundColor:
                            max && count + item > max
                              ? 'gray'
                              : Colors.RedPallete.red,
                        },
                      ]}>
                      + {item}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {!!count && (
                <View style={[styles.container, {padding: 0}, isDesktop && { maxWidth: 500, alignSelf: 'center', width: '100%', borderWidth: 1, borderColor: '#eee', borderRadius: 8, marginTop: 12 }]}>
                  <View style={{padding: 20}}>
                    <Text style={styles?.title2}>Resumo</Text>
                    <View style={styles?.row}>
                      <Text style={styles?.label}>Subtotal</Text>
                      <Text style={styles?.value}>
                        {formatPrice((price * count)?.toFixed(2)?.toString())}
                      </Text>
                    </View>
                    {discountedPrice &&
                      price * count - discountedPrice * count > 0 && (
                        <View style={styles?.row}>
                          <Text style={styles?.label}>Desconto</Text>
                          <Text style={styles?.value}>
                            {discountedPrice
                              ? formatPrice(
                                  (price * count - discountedPrice * count)?.toFixed(2)?.toString(),
                                )
                              : 0}
                          </Text>
                        </View>
                      )}
                    <View style={styles?.row}>
                      <Text style={[styles?.label, styles?.totalLabel]}>
                        Total
                      </Text>
                      <Text style={[styles?.value, styles?.totalValue]}>
                        {formatPrice(
                          discountedPrice
                            ? (discountedPrice * count)?.toFixed(2)?.toString()
                            : (price * count)?.toFixed(2)?.toString(),
                        )}
                      </Text>
                    </View>

                    <TouchableOpacity
                      onPress={() => handleAddBag(item, count)}
                      disabled={toast}>
                      <Text style={styles.confirmButton}>
                        {products.find(
                          prod =>
                            ((prod as any)?.cartKey ?? prod.sku) ===
                            ((item as any)?.cartKey ?? item?.sku),
                        )
                          ? 'Atualizar sacola'
                          : 'Adicionar a sacola'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  {item.hasOwnProperty('box_detail') &&
                    item.box_detail != null && (
                      <Text style={[styles.helperText, {}]}>
                        *Para comprar uma caixa adicione{' '}
                        {item.box_detail.quantity} unidades.
                      </Text>
                    )}
                </View>
              )}
            </>
          )}
          {item?.estoque <= 0 && (
            <ButtonComponent
              title="Avise-me"
              width="100%"
              onPress={() => openModal()}
            />
          )}

          {isCombo &&
            (item.products?.length > 0 || item?.comboProducts?.length > 0) && (
              <ComboProductsList
                products={item.products || item?.comboProducts}
                totalDiscountedPrice={discountedPrice || price}
                totalOriginalPrice={originalComboPrice || null}
                percentageDiscounted={discountPercentage || null}
              />
            )}
         <AviseMeModal
          onPress={handleCloseModal}
          sku={item.sku}
          descricao={item.descricao}
          visible={aviseMeModal}
        />
        </ScrollView>
      </KeyboardAvoidingView>
    </PressCloseKeyboard>
  );
};

export default ProductScreen;
